/** @jsxImportSource @emotion/react */

import IconButton from '@mui/material/IconButton'
import Switch from '@mui/material/Switch'
import React, { FC } from 'react'
import { Link } from 'react-router-dom'
import { findAllPracticeTest_findAllPracticeTest_items as PracticeTest } from '__generatedTypes__/findAllPracticeTest'
import { MembershipType } from '__generatedTypes__/globalTypes'
import { ReactComponent as EditIcon } from 'assets/icons/edit.svg'
import Table from 'components/Table'
import TableButtons from 'components/TableButtons/TableButtons'
import { Tooltip } from 'components/Tooltip'
import * as classes from '../styles'

export const getMembershipAccess = (membershipLevel: MembershipType) => {
  switch (membershipLevel) {
    case MembershipType.PremiumMax:
      return 'Premium Membership Max'
    case MembershipType.Premium:
      return 'Premium Membership, Premium Membership Max'
    case MembershipType.Free:
    default:
      return 'Free, Premium Membership, Premium Membership Max'
  }
}

type Props = {
  title: string
  practiceTests: PracticeTest[]
  submittingPracticeTestId?: string
  disableTableButtons?: boolean
  onIsLiveChange: (practiceTest: PracticeTest, isLive: boolean) => void
  onAddClick: () => void
  getEditLink: (practiceTestId: string) => string
  alwaysDisplayLiveSwitch?: boolean
  currentlyAccessingCount?: Record<string, number>
  settings?: {
    hideAccessColumn?: boolean
    hideLiveColumn?: boolean
    showCurrentlyAccessingColumn?: boolean
  }
}

export const PracticeTestsTable: FC<Props> = ({
  title,
  practiceTests,
  submittingPracticeTestId,
  disableTableButtons,
  onIsLiveChange,
  onAddClick,
  getEditLink,
  alwaysDisplayLiveSwitch = false,
  currentlyAccessingCount = {},
  settings = {},
}) => {
  const firstNotLiveIndex = practiceTests.findIndex(({ isLive }) => !isLive)

  const columns = [
    {
      title: 'Title',
      renderCell: (practiceTest: PracticeTest) => practiceTest.title,
      flex: 1,
    },
    {
      title: 'Access',
      renderCell: (practiceTest: PracticeTest) => getMembershipAccess(practiceTest.membershipLevel),
      flex: 1,
      hidden: settings.hideAccessColumn,
    },
    {
      title: '# of Questions',
      renderCell: (practiceTest: PracticeTest) => String(practiceTest.questionIds.length),
      flex: '0 1 120px',
    },
    {
      title: '# Currently Accessing',
      renderCell: (practiceTest: PracticeTest) => String(currentlyAccessingCount[practiceTest.id] ?? 0),
      flex: '0 1 175px',
      hidden: !settings.showCurrentlyAccessingColumn,
    },
    {
      title: 'Live',
      renderCell: (practiceTest: PracticeTest, index: number) => {
        const canNotBeLive = firstNotLiveIndex !== -1 && index > firstNotLiveIndex

        if (!alwaysDisplayLiveSwitch && (practiceTest.isLive || canNotBeLive)) {
          return (
            <Tooltip
              title={
                canNotBeLive
                  ? `You cannot enable Live for this ${title} as it may disrupt order for Full/Mini Tests, please turn on Live for ${practiceTests[firstNotLiveIndex].title} first`
                  : `You cannot disable Live for this ${title} since it's already used for Full/Mini Tests`
              }
              isNote
              arrow
            >
              <Switch
                checked={practiceTest.isLive}
                onClick={(e) => e.stopPropagation()}
                onChange={(e, isLive) => onIsLiveChange(practiceTest, isLive)}
                disabled
              />
            </Tooltip>
          )
        }

        return (
          <Switch
            checked={practiceTest.isLive}
            onClick={(e) => e.stopPropagation()}
            onChange={(e, isLive) => onIsLiveChange(practiceTest, isLive)}
            disabled={practiceTest.id === submittingPracticeTestId}
          />
        )
      },
      flex: '0 1 50px',
      hidden: settings.hideLiveColumn,
    },
    {
      renderCell: (practiceTest: PracticeTest) => (
        <Link to={getEditLink(practiceTest.id)}>
          <IconButton size="small" css={classes.link}>
            <EditIcon />
          </IconButton>
        </Link>
      ),
      flex: '0 1 30px',
    },
  ].filter((column) => !column.hidden)

  return (
    <>
      <Table columns={columns} items={practiceTests} isSortable={false} />
      <TableButtons itemName="Test" onAddItem={onAddClick} disabled={disableTableButtons} />
    </>
  )
}
